<template>
  <div v-if="hasValidation" class="validation-status">
    <!-- Badge de statut principal -->
    <div class="flex items-center gap-2 mb-2">
      <div :class="validationStatusClass" class="w-2 h-2 rounded-full flex-shrink-0"></div>
      <span class="text-sm font-medium" :class="validationStatusTextClass">
        {{ validationStatusText }}
      </span>
      <!-- Bouton pour voir les détails sur mobile -->
      <button v-if="isMobile && hasMessages" 
        @click="toggleDetails"
        class="ml-auto text-xs text-blue-600 hover:text-blue-700 touch-manipulation">
        {{ showDetails ? $t('charts.validation.hide_details') : $t('charts.validation.show_details') }}
      </button>
    </div>
    
    <!-- Messages de validation -->
    <Transition name="slide-down">
      <div v-if="!isMobile || showDetails" class="validation-messages space-y-1">
        <!-- Messages d'erreur -->
        <div v-for="error in validationResult.errors" :key="error.id"
          class="text-xs p-2 rounded-md bg-red-50 text-red-700 border-l-2 border-red-200">
          <div class="flex items-start gap-2">
            <span class="text-red-500">❌</span>
            <span>{{ getMessage(error) }}</span>
          </div>
        </div>

        <!-- Messages d'avertissement -->
        <div v-for="warning in visibleWarnings" :key="warning.id"
          class="text-xs p-2 rounded-md bg-yellow-50 text-yellow-700 border-l-2 border-yellow-200">
          <div class="flex items-start gap-2">
            <span class="text-yellow-500">⚠️</span>
            <span>{{ getMessage(warning) }}</span>
          </div>
        </div>

        <!-- Messages d'information -->
        <div v-for="info in visibleInfos" :key="info.id"
          class="text-xs p-2 rounded-md bg-blue-50 text-blue-700 border-l-2 border-blue-200">
          <div class="flex items-start gap-2">
            <span class="text-blue-500">💡</span>
            <span>{{ getMessage(info) }}</span>
          </div>
        </div>

        <!-- Bouton pour afficher plus d'avertissements -->
        <button v-if="hiddenWarningsCount > 0"
          @click="showMoreWarnings = true"
          class="text-xs text-gray-600 hover:text-gray-800 mt-1 touch-manipulation">
          + {{ hiddenWarningsCount }} avertissements supplémentaires
        </button>
      </div>
    </Transition>

    <!-- Actions rapides pour corriger les erreurs -->
    <div v-if="validationResult.errors.length > 0 && quickActions.length > 0" 
      class="mt-3 pt-3 border-t border-gray-200">
      <div class="text-xs text-gray-600 mb-2">Actions rapides :</div>
      <div class="flex flex-wrap gap-2">
        <button v-for="action in quickActions" :key="action.id"
          @click="executeQuickAction(action)"
          class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 touch-manipulation">
          {{ action.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ValidationResult, ValidationRule } from '../../utils/chartValidation'
import type { FlexibleChartConfig } from '../../types/customCharts'

interface QuickAction {
  id: string
  label: string
  action: () => void
}

interface Props {
  validationResult: ValidationResult
  config?: FlexibleChartConfig
  isMobile?: boolean
  maxWarnings?: number
  maxInfos?: number
  showQuickActions?: boolean
}

interface Emits {
  (e: 'quickAction', actionId: string): void
  (e: 'configUpdate', config: Partial<FlexibleChartConfig>): void
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  maxWarnings: 3,
  maxInfos: 2,
  showQuickActions: true
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

// État local
const showDetails = ref(false)
const showMoreWarnings = ref(false)

// Status de validation
const hasValidation = computed(() => {
  return props.validationResult.errors.length > 0 || 
         props.validationResult.warnings.length > 0 || 
         props.validationResult.infos.length > 0
})

const hasMessages = computed(() => hasValidation.value)

const validationStatusClass = computed(() => {
  if (props.validationResult.errors.length > 0) return 'bg-red-500'
  if (props.validationResult.warnings.length > 0) return 'bg-yellow-500'
  if (props.validationResult.infos.length > 0) return 'bg-blue-500'
  return 'bg-green-500'
})

const validationStatusTextClass = computed(() => {
  if (props.validationResult.errors.length > 0) return 'text-red-700'
  if (props.validationResult.warnings.length > 0) return 'text-yellow-700'
  if (props.validationResult.infos.length > 0) return 'text-blue-700'
  return 'text-green-700'
})

const validationStatusText = computed(() => {
  const { errors, warnings, infos } = props.validationResult
  
  if (errors.length > 0) {
    return props.isMobile 
      ? `${errors.length} erreur${errors.length > 1 ? 's' : ''}` 
      : `${errors.length} erreur${errors.length > 1 ? 's' : ''} détectée${errors.length > 1 ? 's' : ''}`
  }
  
  if (warnings.length > 0) {
    return props.isMobile
      ? `${warnings.length} avertissement${warnings.length > 1 ? 's' : ''}`
      : `${warnings.length} avertissement${warnings.length > 1 ? 's' : ''}`
  }
  
  if (infos.length > 0) {
    return props.isMobile
      ? `${infos.length} suggestion${infos.length > 1 ? 's' : ''}`
      : `${infos.length} suggestion${infos.length > 1 ? 's' : ''} disponible${infos.length > 1 ? 's' : ''}`
  }
  
  return t('charts.validation.valid_config')
})

// Messages visibles selon les limites
const visibleWarnings = computed(() => {
  const limit = showMoreWarnings.value ? props.validationResult.warnings.length : props.maxWarnings
  return props.validationResult.warnings.slice(0, limit)
})

const visibleInfos = computed(() => {
  // Ne pas afficher les infos s'il y a des erreurs ou beaucoup d'avertissements
  if (props.validationResult.errors.length > 0 || props.validationResult.warnings.length > 2) {
    return []
  }
  return props.validationResult.infos.slice(0, props.maxInfos)
})

const hiddenWarningsCount = computed(() => {
  return Math.max(0, props.validationResult.warnings.length - props.maxWarnings)
})

// Actions rapides pour corriger les erreurs courantes
const quickActions = computed((): QuickAction[] => {
  if (!props.showQuickActions || !props.config) return []

  const actions: QuickAction[] = []

  // Action pour limiter les données si performance warning
  if (props.validationResult.warnings.some(w => w.id === 'performance-warning')) {
    actions.push({
      id: 'limit-data',
      label: '⚡ Limiter à 500 éléments',
      action: () => {
        const updatedConfig = {
          dataSources: props.config!.dataSources.map(ds => ({
            ...ds,
            limit: 500
          }))
        }
        emit('configUpdate', updatedConfig)
      }
    })
  }

  // Action pour supprimer les séries en excès
  if (props.validationResult.errors.some(e => e.id === 'pie-single-series')) {
    actions.push({
      id: 'remove-extra-series',
      label: '🔧 Garder 1 série',
      action: () => {
        const updatedConfig = {
          dataSources: props.config!.dataSources.slice(0, 1)
        }
        emit('configUpdate', updatedConfig)
      }
    })
  }

  // Action pour désactiver l'axe secondaire
  if (props.validationResult.errors.some(e => e.id === 'dual-axis-pie-error')) {
    actions.push({
      id: 'remove-secondary-axis',
      label: '🔧 Supprimer axe secondaire',
      action: () => {
        const updatedConfig = { yAxis2: undefined }
        emit('configUpdate', updatedConfig)
      }
    })
  }

  return actions
})

// Méthodes
const toggleDetails = () => {
  showDetails.value = !showDetails.value
}

const getMessage = (rule: ValidationRule): string => {
  return (props.isMobile && rule.mobileMessage) ? rule.mobileMessage : rule.message
}

const executeQuickAction = (action: QuickAction) => {
  action.action()
  emit('quickAction', action.id)
}

// Masquer les détails automatiquement quand tout devient valide
watch(() => props.validationResult.valid, (isValid) => {
  if (isValid && props.isMobile) {
    showDetails.value = false
  }
})
</script>

<style scoped>
@reference "../../style.css";

/* Animation slide down */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top;
}

.slide-down-enter-from {
  opacity: 0;
  transform: scaleY(0.8) translateY(-8px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: scaleY(0.8) translateY(-8px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: scaleY(1) translateY(0);
}

/* Optimisations tactiles */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Améliorations pour la lisibilité mobile */
@media (max-width: 640px) {
  .validation-messages {
    font-size: 0.75rem;
  }
  
  .validation-messages .p-2 {
    padding: 0.5rem;
  }
  
  .validation-messages .gap-2 {
    gap: 0.375rem;
  }
}

/* États hover seulement pour desktop */
@media (hover: hover) {
  .hover\:text-blue-700:hover {
    color: rgb(29 78 216);
  }
  
  .hover\:text-gray-800:hover {
    color: rgb(31 41 55);
  }
  
  .hover\:bg-blue-200:hover {
    background-color: rgb(191 219 254);
  }
}

/* Bordures gauches plus visibles */
.border-l-2 {
  border-left-width: 3px;
}

/* Espacement des messages */
.validation-messages .space-y-1 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.375rem;
}
</style>